import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  CashFunding,
  CashFundingInsert,
  CashFundingPostConfirm,
  CashFundingUpdate,
  FundableCashRequest,
} from '../models/cash-funding/cash-funding';

@Injectable({ providedIn: 'root' })
export class CashFundingProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/cash-fundings';

  /** All CashFundings for the current company (dashboard list). */
  public getAll(): Observable<ApiResponse<CashFunding[]>> {
    return this.httpClient.get<ApiResponse<CashFunding[]>>(this.baseUrl);
  }

  /** Approved Cash Requests (current company) that have no CashFunding yet. */
  public getFundableRequests(): Observable<ApiResponse<FundableCashRequest[]>> {
    return this.httpClient.get<ApiResponse<FundableCashRequest[]>>(`${this.baseUrl}/fundable-requests`);
  }

  public getById(id: string): Observable<ApiResponse<CashFunding>> {
    return this.httpClient.get<ApiResponse<CashFunding>>(`${this.baseUrl}/${id}`);
  }

  public createDraft(data: CashFundingInsert): Observable<ApiResponse<CashFunding>> {
    return this.httpClient.post<ApiResponse<CashFunding>>(this.baseUrl, data);
  }

  /** Update a Draft funding's editable fields (destination bank, amount, fees, date, bank reference). */
  public updateDraft(id: string, data: CashFundingUpdate): Observable<ApiResponse<CashFunding>> {
    return this.httpClient.put<ApiResponse<CashFunding>>(`${this.baseUrl}/${id}`, data);
  }

  public post(id: string, confirm: CashFundingPostConfirm): Observable<ApiResponse<CashFunding>> {
    return this.httpClient.post<ApiResponse<CashFunding>>(`${this.baseUrl}/${id}/post`, confirm);
  }

  /** Attach a funding document (wire confirmation / transfer receipt) to an existing funding (multipart). */
  public uploadAttachment(id: string, file: File): Observable<ApiResponse<CashFunding>> {
    const form = new FormData();
    form.append('file', file);
    return this.httpClient.post<ApiResponse<CashFunding>>(`${this.baseUrl}/${id}/receipt`, form);
  }
}
